import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCheck, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round(n * 100) / 100;

export default function ExpenseTracker() {
  const { id } = useParams();
  const [expenses, setExpenses] = useState([]);
  const [staffRoles, setStaffRoles] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState({ category: "", amount: "" });
  const [adding, setAdding] = useState(false);
  const [openRoles, setOpenRoles] = useState({});
  const [expandedWorkers, setExpandedWorkers] = useState({});
  const toggleWorkerBreakdown = (appId) => setExpandedWorkers(e => ({ ...e, [appId]: !e[appId] }));
  const [travelDetailOpen, setTravelDetailOpen] = useState({});
  const toggleTravelDetail = (appId) => setTravelDetailOpen(t => ({ ...t, [appId]: !t[appId] }));
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetInput, setBudgetInput] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);

  // Generic in-flight tracker for every financial mutation (delete / toggle-paid / bulk-paid),
  // so buttons disable during the request and a stray double-click can't fire it twice.
  const [processingIds, setProcessingIds] = useState(new Set());
  const isProcessing = (key) => processingIds.has(key);
  const withProcessing = async (key, fn) => {
    setProcessingIds(prev => new Set(prev).add(key));
    try {
      await fn();
    } catch {
      toast.error("Action failed. Please try again.");
    } finally {
      setProcessingIds(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const load = async () => {
    try {
      const [expRes, evRes] = await Promise.all([
        api.get(`/events/${id}/expenses`),
        api.get(`/events/${id}`)
      ]);
      setExpenses(expRes.data.expenses);
      setStaffRoles(expRes.data.staff_roles || []);
      setEvent(evRes.data);
    } catch {
      toast.error("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addExpense = async () => {
    if (!newRow.category.trim()) { toast.error("Enter category name"); return; }
    const amt = parseFloat(newRow.amount);
    if (!newRow.amount || isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount greater than 0"); return; }
    setAdding(true);
    try {
      await api.post(`/events/${id}/expenses`, {
        category: newRow.category.trim(),
        amount: round2(amt),
        paid: false,
      });
      setNewRow({ category: "", amount: "" });
      load();
    } catch { toast.error("Failed to add expense"); }
    finally { setAdding(false); }
  };

  const togglePaid = (exp) =>
    withProcessing(`exp:${exp.id}`, async () => {
      await api.patch(`/expenses/${exp.id}`, { paid: !exp.paid });
      await load();
    });

  const deleteExpense = (expId) => {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    return withProcessing(`exp:${expId}`, async () => {
      await api.delete(`/expenses/${expId}`);
      await load();
    });
  };

  const toggleWorkerPaid = (appId, currentPaid) =>
    withProcessing(`worker:${appId}`, async () => {
      await api.patch(`/applications/${appId}/payment`, { paid: !currentPaid });
      await load();
    });

  const markRolePaid = (jobId) => {
    if (!window.confirm("Mark every worker in this role as paid?")) return;
    return withProcessing(`role:${jobId}`, async () => {
      await api.post(`/events/${id}/expenses/mark-role-paid`, { job_id: jobId });
      toast.success("All workers in this role marked as paid!");
      await load();
    });
  };

  const markAllPaid = () => {
    if (!window.confirm("Mark every expense and every worker as paid?")) return;
    return withProcessing("all", async () => {
      await api.post(`/events/${id}/expenses/mark-all-paid`);
      toast.success("All expenses marked as paid!");
      await load();
    });
  };

  const openBudgetEdit = () => {
    setBudgetInput(event?.budget != null ? String(event.budget) : "");
    setEditingBudget(true);
  };

  const saveBudget = async () => {
    const b = parseFloat(budgetInput);
    if (budgetInput === "" || isNaN(b) || b < 0) { toast.error("Enter a valid budget amount"); return; }
    setSavingBudget(true);
    try {
      await api.patch(`/events/${id}`, { budget: round2(b) });
      toast.success("Budget updated");
      setEditingBudget(false);
      load();
    } catch { toast.error("Failed to update budget"); }
    finally { setSavingBudget(false); }
  };

  const toggleRole = (jobId) => {
    setOpenRoles(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  // TODO(root cause pending): staffPaid sums w.payment (gross salary) while the row UI displays
  // w.net_payment (salary - fine + travel). These will diverge for any worker with a nonzero
  // fine or travel_expense. Needs the backend /events/{id}/expenses handler to confirm whether
  // role.total is gross or net before this can be corrected — do not change silently.
  const { expenseTotal, expensePaid } = useMemo(() => {
    let t = 0, p = 0;
    for (const e of expenses) {
      const amt = e.amount || 0;
      t += amt;
      if (e.paid) p += amt;
    }
    return { expenseTotal: t, expensePaid: p };
  }, [expenses]);

  const { staffTotal, staffPaid } = useMemo(() => {
    let t = 0, p = 0;
    for (const r of staffRoles) {
      t += r.total;
      for (const w of r.workers) if (w.paid) p += w.payment; // see TODO above
    }
    return { staffTotal: t, staffPaid: p };
  }, [staffRoles]);

  const total = expenseTotal + staffTotal;
  const paid = expensePaid + staffPaid;
  const unpaid = total - paid;

  const hasBudget = event?.budget != null;
  const budget = hasBudget ? Number(event.budget) : 0;
  const balance = hasBudget ? budget - total : null;
  const overBudget = hasBudget && balance < 0;
  const balanceCash = hasBudget ? budget - paid : null;

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <p className="text-sm text-muted-foreground">Loading expenses…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
      <Link to={`/manage-event/${id}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brand mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to event
      </Link>

      <span className="label-eyebrow text-muted-foreground">Expense tracker</span>
      <h1 className="mt-1 font-display text-3xl tracking-tighter">{event?.name || "Event"}</h1>

      <div className="mt-6 border border-border rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_120px_80px_40px] gap-2 px-4 py-3 bg-muted text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          <span>Category</span>
          <span>Amount (₹)</span>
          <span>Paid?</span>
          <span></span>
        </div>

        {staffRoles.map(role => (
          <div key={role.job_id} className="border-t border-border">
            <div className="grid grid-cols-[1fr_120px_80px_40px] gap-2 px-4 py-3 items-center bg-brand-tint/10">
              <button
                onClick={() => toggleRole(role.job_id)}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-brand transition text-left"
              >
                {openRoles[role.job_id]
                  ? <ChevronUp className="w-4 h-4 flex-shrink-0" />
                  : <ChevronDown className="w-4 h-4 flex-shrink-0" />}
                {role.role}
                <span className="text-xs text-muted-foreground">({role.workers.length} present)</span>
              </button>
              <span className="text-sm font-medium text-foreground">₹{fmt(role.total)}</span>
              <span className={`text-xs px-2 py-1 rounded-full border font-medium ${
                role.all_paid ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-background text-muted-foreground border-border"
              }`}>
                {role.all_paid ? "✓ Paid" : "Partial"}
              </span>
              <span></span>
            </div>

            {openRoles[role.job_id] && (
              <div className="bg-muted/20 border-t border-border">
                {role.workers.map(worker => (
                  <div key={worker.app_id}>
                    <div className="grid grid-cols-[1fr_120px_80px_40px] gap-2 px-6 py-2.5 items-center border-b border-border/50 last:border-0">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-brand-tint grid place-items-center text-xs font-bold text-brand flex-shrink-0">
                          {(worker.user_name || "?")[0]}
                        </div>
                        <div>
                          <p className="text-sm text-foreground">{worker.user_name}</p>
                          <p className="text-xs text-muted-foreground">@{worker.user_username}</p>
                        </div>
                      </div>
                      <span className="text-sm text-foreground flex items-center justify-end gap-1">
                        {(worker.fine_amount > 0 || worker.travel_expense > 0) && (
                          <button
                            type="button"
                            onClick={() => toggleWorkerBreakdown(worker.app_id)}
                            aria-expanded={!!expandedWorkers[worker.app_id]}
                            className="text-muted-foreground hover:text-foreground flex-shrink-0"
                          >
                            {expandedWorkers[worker.app_id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </button>
                        )}
                        ₹{fmt(worker.net_payment)}
                      </span>
                      <button
                        onClick={() => toggleWorkerPaid(worker.app_id, worker.paid)}
                        disabled={isProcessing(`worker:${worker.app_id}`)}
                        className={`text-xs px-2 py-1 rounded-full border transition font-medium disabled:opacity-50 ${
                          worker.paid ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-background text-muted-foreground border-border"
                        }`}
                      >
                        {worker.paid ? "✓ Paid" : "Unpaid"}
                      </button>
                      <span></span>
                    </div>
                    {expandedWorkers[worker.app_id] && (worker.fine_amount > 0 || worker.travel_expense > 0) && (
                      <div className="px-6 pb-2.5 -mt-1 border-b border-border/50 last:border-0">
                        <div className="ml-9 max-w-xs grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                          <span>Salary</span><span className="text-right">₹{fmt(worker.payment)}</span>
                          {worker.fine_amount > 0 && (
                            <>
                              <span className="text-red-500">− Fine</span>
                              <span className="text-right text-red-500">₹{fmt(worker.fine_amount)}</span>
                            </>
                          )}
                          {worker.travel_expense > 0 && (
                          <>
                            <button
                              type="button"
                              onClick={() => toggleTravelDetail(worker.app_id)}
                              className="text-emerald-600 text-left flex items-center gap-0.5"
                            >
                              + Travel
                              {travelDetailOpen[worker.app_id] ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                            </button>
                            <span className="text-right text-emerald-600">₹{fmt(worker.travel_expense)}</span>
                            {travelDetailOpen[worker.app_id] && worker.travel_distance_km != null && (
                              <p className="col-span-2 text-[10px] text-muted-foreground -mt-0.5">
                                {worker.travel_distance_km} km from {worker.travel_distance_source === "home" ? "Home" : "Centre"} × ₹{worker.travel_rate}/km
                              </p>
                            )}
                          </>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div className="px-6 py-2">
                  <button
                    onClick={() => markRolePaid(role.job_id)}
                    disabled={isProcessing(`role:${role.job_id}`)}
                    className="text-xs text-brand hover:underline font-medium disabled:opacity-50"
                  >
                    Mark all in {role.role} as paid
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {expenses.length === 0 && staffRoles.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No expenses yet. Add your first expense below.
          </div>
        )}
        {expenses.map(exp => (
          <div key={exp.id} className="grid grid-cols-[1fr_120px_80px_40px] gap-2 px-4 py-3 border-t border-border items-center">
            <span className="text-sm text-foreground">{exp.category}</span>
            <span className="text-sm font-medium text-foreground">₹{fmt(exp.amount)}</span>
            <button
              onClick={() => togglePaid(exp)}
              disabled={isProcessing(`exp:${exp.id}`)}
              className={`text-xs px-2 py-1 rounded-full border transition font-medium disabled:opacity-50 ${
                exp.paid ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-background text-muted-foreground border-border"
              }`}>
              {exp.paid ? "✓ Paid" : "Unpaid"}
            </button>
            <button
              onClick={() => deleteExpense(exp.id)}
              disabled={isProcessing(`exp:${exp.id}`)}
              className="text-muted-foreground hover:text-red-500 transition disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}

        <div className="grid grid-cols-[1fr_120px_80px_40px] gap-2 px-4 py-3 border-t border-border items-center bg-muted/30">
          <input
            className="input-base !py-2 text-sm"
            placeholder="Category name…"
            value={newRow.category}
            onChange={e => setNewRow(r => ({ ...r, category: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && addExpense()}
          />
          <input
            type="number"
            min="0.01"
            step="0.01"
            className="input-base !py-2 text-sm"
            placeholder="Amount"
            value={newRow.amount}
            onChange={e => setNewRow(r => ({ ...r, amount: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && addExpense()}
          />
          <button onClick={addExpense} disabled={adding} className="btn-primary !py-2 text-xs flex items-center gap-1 disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
          <span></span>
        </div>
      </div>

      <div className="mt-6 card-flat">
        {editingBudget ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-base !py-2 text-sm flex-1"
              placeholder="Enter budget amount"
              value={budgetInput}
              onChange={e => setBudgetInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && saveBudget()}
              autoFocus
            />
            <button onClick={saveBudget} disabled={savingBudget} className="btn-primary !py-2 text-xs disabled:opacity-50">
              {savingBudget ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditingBudget(false)} disabled={savingBudget} className="btn-outline !py-2 text-xs disabled:opacity-50">Cancel</button>
          </div>
        ) : hasBudget ? (
          <>
            <div className="text-center mb-4 relative">
              <p className="text-xs text-muted-foreground label-eyebrow">Budget</p>
              <p className="font-display text-2xl text-foreground mt-1">₹{fmt(budget)}</p>
              <button onClick={openBudgetEdit} className="text-xs text-brand hover:underline mt-1">Edit budget</button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center pt-3 border-t border-border">
              <div>
                <p className={`text-xs label-eyebrow ${overBudget ? "text-red-500" : "text-emerald-600"}`}>
                  {overBudget ? "Overcommitted by" : "Balance (planned)"}
                </p>
                <p className={`font-display text-xl mt-1 ${overBudget ? "text-red-500" : "text-emerald-600"}`}>
                  ₹{fmt(Math.abs(balance))}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Budget minus all expenses</p>
              </div>
              <div>
                <p className={`text-xs label-eyebrow ${balanceCash < 0 ? "text-red-500" : "text-brand"}`}>
                  {balanceCash < 0 ? "Cash overspent by" : "Balance (cash)"}
                </p>
                <p className={`font-display text-xl mt-1 ${balanceCash < 0 ? "text-red-500" : "text-brand"}`}>
                  ₹{fmt(Math.abs(balanceCash))}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Budget minus amount paid</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">No budget set for this event.</p>
            <button onClick={openBudgetEdit} className="btn-primary text-sm">Set budget</button>
          </div>
        )}
      </div>

      {(expenses.length > 0 || staffRoles.length > 0) && (
        <div className="mt-4 card-flat">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground label-eyebrow">Total spent</p>
              <p className="font-display text-2xl text-foreground mt-1">₹{fmt(total)}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-600 label-eyebrow">Paid</p>
              <p className="font-display text-2xl text-emerald-600 mt-1">₹{fmt(paid)}</p>
            </div>
            <div>
              <p className="text-xs text-amber-500 label-eyebrow">Unpaid</p>
              <p className="font-display text-2xl text-amber-500 mt-1">₹{fmt(unpaid)}</p>
            </div>
          </div>
          <button
            onClick={markAllPaid}
            disabled={isProcessing("all")}
            className="btn-primary w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <CheckCheck className="w-4 h-4" /> Mark all as paid
          </button>
        </div>
      )}
    </div>
  );
}